import { useEffect, useState } from "react";
import axios from "axios";
import { Quote, MapPin, Star } from "lucide-react";
import { Reveal } from "./Reveal";

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;

  const rated = reviews.filter((r) => r.rating);
  const avg = rated.length ? rated.reduce((s, r) => s + r.rating, 0) / rated.length : 0;

  return (
    <section data-testid="reviews-section" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">Reviews</p>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">
            What our customers say.
          </h2>
          <p className="mt-4 text-brand-muted">
            Genuine words from real KAC customers — published exactly as they were given to us.
          </p>
          {rated.length >= 2 && (
            <div data-testid="average-rating-badge" className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-full border border-brand-light bg-white px-5 py-2.5 shadow-sm">
              <span className="flex gap-0.5" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`h-5 w-5 ${n <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-brand-light"}`} />
                ))}
              </span>
              <span className="font-heading text-sm font-extrabold text-brand-green">{avg.toFixed(1)} out of 5</span>
              <span className="text-xs font-semibold text-brand-muted">from {rated.length} customer reviews</span>
            </div>
          )}
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08} className="h-full">
              <figure data-testid={`review-card-${r.id.slice(0, 8)}`} className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-light bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                {r.image_path && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.image_path}`}
                    alt={`Finished KAC work reviewed by ${r.name}`}
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover"
                  />
                )}
                <div className="flex flex-1 flex-col p-7">
                <Quote className="h-7 w-7 text-brand-red" strokeWidth={1.8} />
                {r.rating ? (
                  <div className="mt-3 flex gap-1" data-testid={`review-stars-${r.id.slice(0, 8)}`} aria-label={`Rated ${r.rating} out of 5`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-brand-light"}`} />
                    ))}
                  </div>
                ) : null}
                <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-brand-ink/85">“{r.text}”</blockquote>
                <figcaption className="mt-5 border-t border-brand-light pt-4">
                  <p className="font-heading text-sm font-bold text-brand-green">{r.name}</p>
                  {r.location && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-muted">
                      <MapPin className="h-3.5 w-3.5 text-brand-red" /> {r.location}
                    </p>
                  )}
                </figcaption>
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
